import React from 'react';
import { Link } from 'react-router-dom';
import Seo from '../components/Seo';
import Base64Tool from '../components/Base64Tool';
import CodeBlock from '../components/CodeBlock';
import FaqSection from '../components/Faq';
import { PageHero } from '../components/InfoSections';
import { useTheme } from '../theme';
import { softwareApplicationSchema, faqPageSchema, breadcrumbSchema, Faq } from '../lib/schema';

const FAQS: Faq[] = [
  { q: 'How do I Base64 encode a CSV file?', a: 'Paste your CSV rows above and switch to Encode, or use the Upload button to load a .csv file. The result is a Base64 string safe to embed in JSON payloads or send over APIs.' },
  { q: 'Why Base64 encode CSV data?', a: 'CSV files can contain commas, quotes and newlines that break JSON or form fields. Base64 wraps the whole file as one plain-text-safe string, commonly used to attach CSV exports to API requests or emails.' },
  { q: 'Does Base64 encoding change my CSV data?', a: 'No. Decoding returns the exact original bytes, so commas, line endings and quoting are preserved exactly as they were.' },
  { q: 'Can I decode a large CSV export this way?', a: 'Yes, but very large files are better handled with the file-based conversion in your programming language (see the code example below) rather than pasting text by hand.' },
];

export default function CsvToBase64Page() {
  const { themeClasses } = useTheme();
  return (
    <>
      <Seo
        title="CSV to Base64 — Encode & Decode CSV Online | EasyBase64"
        description="Encode CSV data to Base64 and decode it back online for free. UTF-8 safe, instant, and fully private. Useful for API uploads and email attachments."
        path="/csv-to-base64"
        jsonLd={[
          softwareApplicationSchema({ name: 'CSV to Base64 Converter', description: 'Encode and decode CSV data as Base64 in the browser.', path: '/csv-to-base64' }),
          faqPageSchema(FAQS),
          breadcrumbSchema([{ name: 'Home', path: '/' }, { name: 'CSV to Base64', path: '/csv-to-base64' }]),
        ]}
      />
      <PageHero
        title="CSV to Base64"
        subtitle={<>Encode CSV rows to Base64 and decode them back. Working with JSON instead? Try <Link to="/json-to-base64" className="underline">JSON to Base64</Link>.</>}
      />
      <div className="mb-12">
        <Base64Tool initialMode="encode" variant="standard" />
      </div>
      <section className={`${themeClasses.infoCard} rounded-2xl p-5 sm:p-8 border mb-12 max-w-3xl mx-auto`}>
        <h2 className={`text-2xl font-bold mb-4 ${themeClasses.textPrimary}`}>Example</h2>
        <p className={`${themeClasses.textSecondary} leading-relaxed mb-4`}>
          The CSV row <code>name,age{'\n'}Ada,36</code> encodes to <code>bmFtZSxhZ2UKQWRhLDM2</code>. In code you would do:
        </p>
        <CodeBlock
          language="JavaScript"
          code={`const csv = "name,age\\nAda,36";
const b64 = btoa(unescape(encodeURIComponent(csv))); // "bmFtZSxhZ2UKQWRhLDM2"
const back = decodeURIComponent(escape(atob(b64)));`}
        />
      </section>
      <FaqSection title="CSV to Base64 — FAQ" faqs={FAQS} />
    </>
  );
}
